import { useState } from 'react';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { useReducedMotion } from 'motion/react';
import { CheckIcon, XIcon } from 'lucide-react';
import type { Agent } from '@/entities/agent';
import { microInteraction } from '@/shared/ui/MicroInteractions';
import { CustomAgentChip } from '@/shared/ui/CustomAgentChip';
import { useTranslation } from 'react-i18next';

// The confirm commits mid-recoil rather than after it finishes, so
// the squish reads as feedback rather than a delay the user waits out.
const CONFIRM_COMMIT_DELAY_MS = 200;

interface EditingShortcutSlotProps {
  slotIndex: number;
  initialAgentId: string | null;
  availableAgents: Agent[];
  isSaving: boolean;
  onCancel: () => void;
  onConfirm: (agentId: string) => void;
}

export function EditingShortcutSlot({
  slotIndex,
  initialAgentId,
  availableAgents,
  isSaving,
  onCancel,
  onConfirm,
}: EditingShortcutSlotProps) {
  const { t } = useTranslation('springboard');
  const shouldReduceMotion = useReducedMotion();

  const [draftAgentId, setDraftAgentId] = useState<string | null>(initialAgentId);
  // Click feedback on confirm: a squish that plays independently of the
  // mutation's own latency, so it's visible even on a fast network.
  const [isRecoiling, setIsRecoiling] = useState(false);

  const handleConfirm = () => {
    if (!draftAgentId) return;
    if (shouldReduceMotion) {
      onConfirm(draftAgentId);
      return;
    }
    setIsRecoiling(true);
    setTimeout(() => onConfirm(draftAgentId), CONFIRM_COMMIT_DELAY_MS);
  };

  // Locks both buttons for the same window the recoil covers, on top of the
  // real network `isSaving` — otherwise a second click could fire during the
  // pre-commit delay, before the mutation has even started.
  const isBusy = isSaving || isRecoiling;

  return (
    <div
      data-testid={`shortcut-slot-${slotIndex}-editing`}
      className="flex h-full w-full flex-col justify-between gap-sm rounded-lg border border-dashed border-border-strong bg-muted/50 p-md"
    >
      <div
        className={`flex items-center justify-end gap-2xs ${microInteraction.shortcutContentInButtons}`}
      >
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="mini"
                aria-label={t('dashboard.editingShortcutSlot.cancel')}
                onClick={onCancel}
                disabled={isBusy}
                className="size-8 p-0"
              >
                <XIcon className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {t('dashboard.editingShortcutSlot.cancel')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="default"
                size="mini"
                aria-label={t('dashboard.editingShortcutSlot.confirm')}
                onClick={handleConfirm}
                disabled={!draftAgentId || isBusy}
                onAnimationEnd={() => setIsRecoiling(false)}
                className={`size-8 p-0 ${isRecoiling ? microInteraction.shortcutRecoil : ''}`}
              >
                <CheckIcon className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {t('dashboard.editingShortcutSlot.confirm')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className={`flex flex-col gap-xs ${microInteraction.shortcutContentInField}`}>
        <span className="text-[13px] leading-4 font-medium text-muted-foreground">
          {initialAgentId
            ? t('dashboard.editingShortcutSlot.editTitle')
            : t('dashboard.editingShortcutSlot.addTitle')}
        </span>
        <Select value={draftAgentId ?? undefined} onValueChange={setDraftAgentId}>
          <SelectTrigger className="w-full bg-card">
            <SelectValue placeholder={t('dashboard.editingShortcutSlot.selectTool')} />
          </SelectTrigger>
          <SelectContent className="max-h-60">
            {availableAgents.map((agent) => (
              <SelectItem key={agent.id} value={agent.id}>
                <span className="flex items-center gap-xs">
                  {agent.title}
                  {agent.visibility && <CustomAgentChip visibility={agent.visibility} />}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
